import React, { useState, useEffect, useMemo } from 'react';
import Modal from '../ui/Modal';
import { mlszAPI } from '../../services/api';
import type { MlszSquadPlayer, MlszLinkResult } from '../../services/api';
import { Player } from '../../types/player';
import { getErrorMessage } from '../../utils/errorMessage';

interface IfaLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  player: Player | null;
  onLinked: () => void;
}

const normalize = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();

/** yyyy.MM.dd az IFA dátumaiból és a sajátunkból, hogy összevethetők legyenek. */
const toComparableDate = (value?: string | null): string => {
  if (!value) return '';
  const parsed = new Date(value);
  if (!isNaN(parsed.getTime())) {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${parsed.getFullYear()}.${pad(parsed.getMonth() + 1)}.${pad(parsed.getDate())}`;
  }
  return value.trim();
};

/**
 * Nálunk létrehozott játékos összekötése egy IFA-belivel - tipikusan akkor, ha
 * a játékos előbb került be hozzánk, és csak utána igazolták le.
 *
 * A valószínű találatok kerülnek felülre (egyező név vagy születési dátum), és
 * a már összekötött IFA-játékosokat nem lehet másodszor felhasználni.
 */
const IfaLinkModal: React.FC<IfaLinkModalProps> = ({ isOpen, onClose, player, onLinked }) => {
  const [squad, setSquad] = useState<MlszSquadPlayer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<MlszSquadPlayer | null>(null);
  const [linking, setLinking] = useState(false);
  const [result, setResult] = useState<MlszLinkResult | null>(null);

  useEffect(() => {
    if (!isOpen || !player) return;

    setSearch('');
    setSelected(null);
    setResult(null);
    setError(null);
    setLoading(true);

    mlszAPI
      .getSquad()
      .then((response) => {
        if (response.success) {
          setSquad(response.players);
        } else {
          setError(response.error ?? 'Az IFA keret lekérdezése nem sikerült.');
        }
      })
      .catch((err) => setError(getErrorMessage(err, 'Az IFA keret lekérdezése nem sikerült.')))
      .finally(() => setLoading(false));
  }, [isOpen, player]);

  /** Egyező név vagy születési dátum alapján valószínű találat. */
  const isLikelyMatch = useMemo(() => {
    if (!player) return () => false;

    const playerName = normalize(player.name);
    const playerBirth = toComparableDate(player.birth);

    return (candidate: MlszSquadPlayer) =>
      normalize(candidate.name) === playerName ||
      (!!playerBirth && toComparableDate(candidate.birth) === playerBirth);
  }, [player]);

  const visible = useMemo(() => {
    const term = normalize(search);
    const filtered = term ? squad.filter((p) => normalize(p.name).includes(term)) : squad;

    // Valószínű találatok elöl, azon belül név szerint
    return [...filtered].sort((a, b) => {
      const aLikely = isLikelyMatch(a) ? 0 : 1;
      const bLikely = isLikelyMatch(b) ? 0 : 1;
      return aLikely !== bLikely ? aLikely - bLikely : a.name.localeCompare(b.name, 'hu');
    });
  }, [squad, search, isLikelyMatch]);

  const handleLink = async (confirmRelink: boolean) => {
    if (!player || !selected) return;

    setLinking(true);
    setError(null);
    try {
      const response = await mlszAPI.linkPlayer(player.id, selected.regKod, confirmRelink);
      setResult(response);
      if (response.success) {
        onLinked();
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Az összekötés nem sikerült.'));
    } finally {
      setLinking(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Összekötés IFA-val">
      {!player ? null : result?.success ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            <span className="font-medium">{player.name}</span> össze van kötve az IFA-val.
          </p>
          {result.changes.length > 0 && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <p className="mb-2 text-sm font-medium text-gray-900">Átvett adatok</p>
              <ul className="space-y-1 text-sm text-gray-700">
                {result.changes.map((change) => (
                  <li key={change.field}>
                    <span className="font-medium">{change.field}:</span>{' '}
                    {change.oldValue ? (
                      <>
                        <span className="text-gray-500 line-through">{change.oldValue}</span>{' '}
                        <span aria-hidden="true">→</span>{' '}
                      </>
                    ) : null}
                    <span>{change.newValue}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              Bezárás
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Válaszd ki, melyik IFA-beli játékos ez: <span className="font-medium">{player.name}</span>
            {player.birth ? ` (${toComparableDate(player.birth)})` : ''}.
          </p>
          <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-800">
            Az összekötéskor az IFA adatai kerülnek át - a név is. Mentés előtt megmutatjuk, mi változik.
          </p>

          {loading ? (
            <p className="py-6 text-center text-sm text-gray-500">Lekérdezés az IFA-ból...</p>
          ) : error ? (
            <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p>
          ) : (
            <>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Keresés név szerint"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />

              <div className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                {visible.length === 0 ? (
                  <p className="p-3 text-sm text-gray-500">Nincs találat.</p>
                ) : (
                  visible.map((candidate) => {
                    const taken = candidate.linkedPlayerId != null && candidate.linkedPlayerId !== player.id;
                    return (
                      <button
                        key={candidate.regKod}
                        type="button"
                        disabled={taken}
                        onClick={() => setSelected(candidate)}
                        className={`flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left text-sm ${
                          selected?.regKod === candidate.regKod ? 'bg-primary-50' : 'hover:bg-gray-50'
                        } ${taken ? 'cursor-not-allowed opacity-60' : ''}`}
                      >
                        <span className="font-medium text-gray-900">{candidate.name}</span>
                        {candidate.birth && <span className="text-xs text-gray-500">{candidate.birth}</span>}
                        {isLikelyMatch(candidate) && !taken && (
                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                            valószínű találat
                          </span>
                        )}
                        {taken && (
                          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600">
                            már összekötve: {candidate.linkedPlayerName}
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>

              {result && !result.success && (
                <p
                  className={`rounded-md p-3 text-sm ${
                    result.relinkConfirmationRequired
                      ? 'bg-amber-50 text-amber-800'
                      : 'bg-red-50 text-red-700'
                  }`}
                >
                  {result.relinkConfirmationRequired
                    ? `Ehhez a játékoshoz már tartozik IFA-kód (${result.previousRegKod}). Biztosan lecseréled?`
                    : result.error}
                </p>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Mégse
                </button>
                <button
                  type="button"
                  disabled={!selected || linking}
                  onClick={() => handleLink(result?.relinkConfirmationRequired === true)}
                  className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {linking
                    ? 'Összekötés...'
                    : result?.relinkConfirmationRequired
                    ? 'Csere megerősítése'
                    : 'Összekötés'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default IfaLinkModal;
